'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInterval } from 'react-use';

import SelectedMovieView from '@/app/[locale]/(default-layout)/_components/SelectedMovieView';
import SliderImages from '@/app/[locale]/(default-layout)/_components/SliderImages';

import TicketLink from '@/components/ticket-shape/TicketLink';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { CachedContentData } from '@/hooks/useCachedContentData';
import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconBack24 } from '@cineamo/ui/src/icons/ic-back';
import { IconForth24 } from '@cineamo/ui/src/icons/ic-forth';
import { IconTicket2_24 } from '@cineamo/ui/src/icons/ic-ticket';
import TwoColumnsOnLg from '@cineamo/ui/src/layout-components/TwoColumnsOnLg';

import CenterLayout from '@cineamo/composables/src/CenterLayout';
import MobileOrDesktopRenderer from '@cineamo/composables/src/MobileOrDesktopRenderer';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

export type HighlightItem = {
    showing: ShowingDto;
    content: CachedContentData;
};

type SpecialsHeroContentProps = Readonly<{
    highlights: HighlightItem[];
    selectedIndex: number;
    onSelectHighlight: (index: number) => void;
}>;

const SpecialsHeroContent: React.FC<SpecialsHeroContentProps> = ({ highlights, selectedIndex, onSelectHighlight }) => {
    const [currentHighlight, setCurrentHighlight] = useState<HighlightItem>(highlights[0]);
    const [nextHighlight, setNextHighlight] = useState<HighlightItem>(highlights[0]);
    const [isFading, setIsFading] = useState(true);
    const [isAutoScrollingEnabled, setIsAutoScrollingEnabled] = useState(true);

    const lastSelectedByComponent = useRef(selectedIndex);

    useEffect(() => {
        if (selectedIndex !== lastSelectedByComponent.current) {
            lastSelectedByComponent.current = selectedIndex;
            const highlight = highlights[selectedIndex];
            if (highlight) {
                if (isFading && highlight !== currentHighlight) {
                    // eslint-disable-next-line react-hooks/set-state-in-effect
                    setNextHighlight(highlight);
                    setIsFading(false);
                } else if (highlight !== nextHighlight) {
                    setCurrentHighlight(highlight);
                    setIsFading(true);
                }
            }
        }
    }, [selectedIndex, highlights, isFading, currentHighlight, nextHighlight]);

    const selectHighlight = useCallback(
        (index: number) => {
            const highlight = highlights[index];
            lastSelectedByComponent.current = index;
            onSelectHighlight(index);
            if (isFading && highlight !== currentHighlight) {
                setNextHighlight(highlight);
                setIsFading(false);
            } else if (highlight !== nextHighlight) {
                setCurrentHighlight(highlight);
                setIsFading(true);
            }
        },
        [highlights, isFading, currentHighlight, nextHighlight, onSelectHighlight]
    );

    useInterval(() => {
        if (highlights.length > 1 && isAutoScrollingEnabled) {
            const nextIndex = (selectedIndex + 1) % highlights.length;
            selectHighlight(nextIndex);
        }
    }, 10000);

    const goToPrevious = useCallback(() => {
        const prevIndex = (selectedIndex - 1 + highlights.length) % highlights.length;
        selectHighlight(prevIndex);
    }, [selectedIndex, highlights.length, selectHighlight]);

    const goToNext = useCallback(() => {
        const nextIndex = (selectedIndex + 1) % highlights.length;
        selectHighlight(nextIndex);
    }, [selectedIndex, highlights.length, selectHighlight]);

    const t = useTranslations();

    return (
        <div
            className="relative"
            onMouseOver={() => setIsAutoScrollingEnabled(false)}
            onMouseOut={() => setIsAutoScrollingEnabled(true)}
            onMouseLeave={() => setIsAutoScrollingEnabled(true)}>
            {/* Desktop layout: backdrop with left gradient, content overlaid */}
            <MobileOrDesktopRenderer
                desktopContent={
                    <div className="relative flex flex-col w-full min-h-500 xl:aspect-[1440/440] bg-cover bg-center bg-no-repeat">
                        <div className="absolute top-0 bottom-0 right-0 left-0 bg-cover bg-center bg-no-repeat gradient-mask-l-0">
                            <SliderImages
                                showSelectedMovie={isFading}
                                selectedContent1={currentHighlight?.content}
                                selectedContent2={nextHighlight?.content}
                                useKenBurstZoom
                            />
                        </div>
                        <div className="relative flex flex-col w-full min-h-400 flex-auto">
                            <div className="flex flex-col flex-auto">
                                <CenterLayout className="flex flex-col flex-auto">
                                    <TwoColumnsOnLg
                                        className="flex-auto w-full"
                                        left={
                                            <div className="flex flex-col flex-auto relative w-full justify-center">
                                                {currentHighlight && (
                                                    <SelectedMovieView
                                                        className={cn(
                                                            'absolute left-0 flex flex-col w-full',
                                                            'transition-all duration-500',
                                                            isFading ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                                        )}
                                                        content={currentHighlight.content}>
                                                        <HighlightChildren highlight={currentHighlight} />
                                                    </SelectedMovieView>
                                                )}
                                                {nextHighlight && (
                                                    <SelectedMovieView
                                                        className={cn(
                                                            'flex flex-col w-full',
                                                            'transition-all duration-500',
                                                            !isFading ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                                        )}
                                                        content={nextHighlight.content}>
                                                        <HighlightChildren highlight={nextHighlight} />
                                                    </SelectedMovieView>
                                                )}
                                            </div>
                                        }
                                        right={<></>}
                                        leftExtraClass="flex-auto lg:flex-none items-center lg:w-[396px]"
                                        rightExtraClass="lg:!w-[calc(100%-396px)]"
                                    />
                                </CenterLayout>
                            </div>
                            {highlights.length > 1 && (
                                <CenterLayout className="relative">
                                    <div className="flex items-center justify-between py-24">
                                        <button
                                            className="flex items-center justify-center size-24 opacity-70 hover:opacity-100 transition-opacity"
                                            onClick={goToPrevious}
                                            aria-label={t('common.previous')}>
                                            <IconBack24 fill="currentColor" />
                                        </button>
                                        <button
                                            className="flex items-center justify-center size-24 opacity-70 hover:opacity-100 transition-opacity"
                                            onClick={goToNext}
                                            aria-label={t('common.next')}>
                                            <IconForth24 fill="currentColor" />
                                        </button>
                                    </div>
                                </CenterLayout>
                            )}
                        </div>
                    </div>
                }
                mobileContent={
                    <div className="relative flex flex-col w-full">
                        {/* Backdrop image with bottom gradient */}
                        <div className="relative w-full h-[226px] gradient-mask-b-[rgba(0,0,0,1)_40%,transparent_100%]">
                            <SliderImages
                                showSelectedMovie={isFading}
                                selectedContent1={currentHighlight?.content}
                                selectedContent2={nextHighlight?.content}
                                useKenBurstZoom
                            />
                        </div>
                        {/* Centered content below image */}
                        <div className="relative flex flex-col items-center text-center px-24 pb-24 -mt-24">
                            <div className="relative w-full">
                                {currentHighlight && (
                                    <div
                                        className={cn(
                                            'absolute left-0 right-0 flex flex-col items-center',
                                            'transition-all duration-500',
                                            isFading ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                        )}>
                                        <MobileHighlightContent highlight={currentHighlight} />
                                    </div>
                                )}
                                {nextHighlight && (
                                    <div
                                        className={cn(
                                            'flex flex-col items-center',
                                            'transition-all duration-500',
                                            !isFading ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                        )}>
                                        <MobileHighlightContent highlight={nextHighlight} />
                                    </div>
                                )}
                            </div>
                        </div>
                        {highlights.length > 1 && (
                            <div className="flex items-center justify-between px-24 pb-12">
                                <button
                                    className="flex items-center justify-center size-24 opacity-70 hover:opacity-100 transition-opacity"
                                    onClick={goToPrevious}
                                    aria-label={t('common.previous')}>
                                    <IconBack24 fill="currentColor" />
                                </button>
                                <button
                                    className="flex items-center justify-center size-24 opacity-70 hover:opacity-100 transition-opacity"
                                    onClick={goToNext}
                                    aria-label={t('common.next')}>
                                    <IconForth24 fill="currentColor" />
                                </button>
                            </div>
                        )}
                    </div>
                }
            />
        </div>
    );
};

type HighlightContentProps = Readonly<{
    highlight: HighlightItem;
}>;

function useHighlightFormatting(showing: ShowingDto) {
    const domainConfiguration = useDomainConfiguration();
    const format = useFormat({ timeZone: domainConfiguration.timeZone });
    const weekday = format.dateTime.formatWeekday(showing.startDatetime, 'short').replace('.', '');
    const date = format.dateTime.formatDayMonth(showing.startDatetime);
    const time = format.dateTime.formatDate_HHmm24(showing.startDatetime);
    return { weekday, date, time };
}

function TicketsButton({
    showing,
    content
}: Readonly<{
    showing: ShowingDto;
    content?: CachedContentData;
}>): React.JSX.Element {
    const t = useTranslations();
    const { weekday, date, time } = useHighlightFormatting(showing);

    return (
        <TicketLink
            showing={showing}
            content={content}>
            <span className="inline-flex items-center gap-8 px-12 py-6 rounded-4 text-body-bold-14 cursor-pointer bg-[var(--primary-color)] text-[var(--on-primary-color)]">
                <IconTicket2_24
                    className="size-20"
                    aria-hidden="true"
                />
                <span aria-hidden="true">{t('tickets.tickets')}</span>
                <span className="sr-only">
                    {weekday}, {date} {time}
                </span>
            </span>
        </TicketLink>
    );
}

function HighlightChildren({ highlight }: HighlightContentProps): React.JSX.Element {
    const { showing, content } = highlight;
    const { weekday, date, time } = useHighlightFormatting(showing);

    return (
        <div className="flex flex-col gap-12 mt-16">
            {showing.highlightText && (
                <div className="text-body-14 opacity-80 line-clamp-5">{showing.highlightText}</div>
            )}
            <div className="text-body-bold-16">
                {weekday}, {date} {time}
            </div>
            <TicketsButton
                showing={showing}
                content={content}
            />
        </div>
    );
}

function MobileHighlightContent({ highlight }: HighlightContentProps): React.JSX.Element {
    const { showing, content } = highlight;
    const { weekday, date, time } = useHighlightFormatting(showing);
    const title = content.title;

    return (
        <>
            {content.logoImage ? (
                <div className="relative mb-10 w-[220px] max-w-full aspect-[2/1]">
                    <Image
                        src={content.logoImage}
                        alt={title ?? ''}
                        fill
                        sizes="220px"
                        className="object-contain object-bottom [filter:drop-shadow(0px_3px_12px_rgba(0,_0,_0,_0.6))]"
                    />
                </div>
            ) : (
                <div className="text-headline-20">{title}</div>
            )}
            {showing.highlightText && (
                <div className="text-body-12 opacity-80 mt-4 line-clamp-3">{showing.highlightText}</div>
            )}
            <div className="text-body-bold-14 pt-6 pb-10">
                {weekday}, {date} {time}
            </div>
            <TicketsButton
                showing={showing}
                content={content}
            />
        </>
    );
}

export default SpecialsHeroContent;
